import React, { useEffect, useState } from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import useDebounce from 'components/hooks/useDebounce';
import useForm from 'components/hooks/useForm';
import useRequestError from 'components/hooks/useRequestError';
import useSidePanel from 'components/hooks/useSidePanel';

import Paginator from 'components/application/Paginator';
import RowBar from 'components/application/RowBar';
import SearchField from 'components/application/SearchField';
import Table from 'components/application/Table';

import { useProjectCoshhContext } from 'components/contexts/ProjectCoshhContext';
import { useBreadBoard } from 'components/contexts/Toaster';
import MasterCoshhRow from 'components/masterCoshh/components/MasterCoshhRow';
import MasterCoshhSdsSidePanel from 'components/masterCoshh/components/MasterCoshhSdsSidePanel';
import MasterCoshhSearchBlankNotice from 'components/masterCoshh/components/MasterCoshhSearchBlankNotice';
import ProjectCoshhDestroyModal from 'components/projectCoshh/components/ProjectCoshhDestroyModal';
import ProjectCoshhSidePanel from 'components/projectCoshh/components/ProjectCoshhSidePanel';

import {
  defaultCoshhDocument,
  formatPersonsAtRiskValues,
  updatedPersonsAtRiskValues,
} from 'components/helpers/resources/coshhDocuments';

const initialMasterCoshhDocumentsState = {
  loaded: false,
  data: [],
  metaData: { currentPage: null, totalPages: null },
};

const initialCoshhDocumentsState = {
  loaded: false,
  data: [],
  included: [],
  allMasterCoshhDocumentIds: {},
};

export default function MasterCoshhTable({ onCoshhDocumentChange }) {
  const breadBoard = useBreadBoard();

  const { comparisonValueForCustomValueUsage, projectId } =
    useProjectCoshhContext();

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'masterCoshh.components.MasterCoshhTable',
  });

  const { t: i18nTCommon } = useTranslation('common');

  const [masterCoshhDocuments, setMasterCoshhDocuments] = useState(
    initialMasterCoshhDocumentsState,
  );

  const [coshhDocuments, setCoshhDocuments] = useState(
    initialCoshhDocumentsState,
  );

  const [
    requestError,
    submitDisabled,
    removeErrorStyling,
    resetRequestError,
    handleRequestError,
  ] = useRequestError();

  const [
    currentCoshhDocument,
    setCurrentCoshhDocument,
    handleCoshhInputChange,
    handleCoshhOptionChange,
    ,
    ,
    handleOptionSelect,
  ] = useForm(defaultCoshhDocument);

  const [initialPersonsAtRisk, setInitialPersonsAtRisk] = useState([]);

  const [isSidePanelSubmitDisabled, setIsSidePanelSubmitDisabled] =
    useState(false);

  const [
    isSidePanelOpen,
    _setIsSidePanelOpen,
    openSidePanel,
    closeSidePanel,
    _resetSidePanelContext,
    sidePanelContext,
    setSidePanelContext,
  ] = useSidePanel(false, 'new');

  const [isSdsSidePanelOpen, , openSdsSidePanel, closeSdsSidePanel] =
    useSidePanel(false);

  const [previewMasterCoshhDocumentId, setPreviewMasterCoshhDocumentId] =
    useState(null);

  const [pageNumber, setPageNumber] = useState(1);

  const [isProjectCoshhDestroyModalOpen, setIsProjectCoshhDestroyModalOpen] =
    useState(false);

  const [currentSearch, setCurrentSearch, handleSearchInputChange] = useForm({
    masterCoshhSearch: '',
  });

  const [debouncedCurrentSearch, _resetDebouncedCurrentSearch] = useDebounce(
    currentSearch,
    250,
  );

  const handleSearchReset = (event) => {
    const name = event.target.getAttribute('data-attr-name');
    setCurrentSearch({ ...currentSearch, [name]: '' });
  };

  const fetchMasterCoshhDocuments = (page = 1) => {
    axios
      .get(`/projects/${projectId}/master_coshh`, {
        params: {
          search: currentSearch.masterCoshhSearch || null,
          page,
        },
      })
      .then((response) => {
        setIsSidePanelSubmitDisabled(false);
        setMasterCoshhDocuments({
          loaded: true,
          data: response.data.data,
          metaData: response.data.meta,
        });
        setPageNumber(response.data.meta.currentPage);
        setCurrentCoshhDocument(defaultCoshhDocument);
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  const fetchCoshhDocuments = () => {
    axios
      .get(`/projects/${projectId}/coshh`)
      .then((response) => {
        closeSidePanel();
        const allMasterCoshhDocumentIds = response.data.data.reduce(
          (acc, obj) => ({
            ...acc,
            [obj.relationships.masterCoshhDocument.data.id]: obj.id,
          }),
          {},
        );

        setCoshhDocuments({
          loaded: true,
          data: response.data.data,
          included: response.data.included,
          allMasterCoshhDocumentIds,
        });
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  const refreshResources = (pageNumber = 1) => {
    fetchCoshhDocuments();
    fetchMasterCoshhDocuments(pageNumber);
  };

  useEffect(() => {
    if (masterCoshhDocuments.loaded && debouncedCurrentSearch) {
      fetchMasterCoshhDocuments();
    }
  }, [debouncedCurrentSearch]);

  useEffect(() => {
    refreshResources();
  }, []);

  const handlePageChange = (event) => {
    const page = event.currentTarget.getAttribute('data-page');
    fetchMasterCoshhDocuments(page);
  };

  const defaultCurrentCoshhDocument = (masterCoshhDocument) => ({
    id:
      coshhDocuments.loaded &&
      coshhDocuments.allMasterCoshhDocumentIds[masterCoshhDocument.id],
    title: masterCoshhDocument.attributes.title,
    masterId: masterCoshhDocument.id,
    coshhVersionId:
      masterCoshhDocument.relationships.latestCoshhVersion.data.id,
    longTermExposureLimit: masterCoshhDocument.attributes.longTermExposureLimit,
    shortTermExposureLimit:
      masterCoshhDocument.attributes.shortTermExposureLimit,
    quantityOnSite: '',
    quantityToBeUsed: '',
    substanceQuantityPerDay: '',
    uses: '',
    methodOfUseOther: '',
    staffNumber: '',
    personsAtRisk: [],
    personsAtRiskOther: '',
    workerExposureTime: '',
    workerExposureTimeOther: '',
    exposureFrequency: '',
    exposureFrequencyOther: '',
    location: '',
    levelOfRisk: '',
    additionalInformation: '',
  });

  const coshhDocumentCreateParams = {
    project_id: projectId,
    coshh_version_id: currentCoshhDocument.coshhVersionId,
    quantity_on_site: currentCoshhDocument.quantityOnSite,
    quantity_to_be_used: currentCoshhDocument.quantityToBeUsed,
    substance_quantity_per_day: currentCoshhDocument.substanceQuantityPerDay,
    uses: currentCoshhDocument.uses,
    method_of_use_other:
      currentCoshhDocument.uses === comparisonValueForCustomValueUsage ?
        currentCoshhDocument.methodOfUseOther
      : '',
    staff_number: currentCoshhDocument.staffNumber,
    worker_exposure_time: currentCoshhDocument.workerExposureTime,
    worker_exposure_time_other:
      (
        currentCoshhDocument.workerExposureTime ===
        comparisonValueForCustomValueUsage
      ) ?
        currentCoshhDocument.workerExposureTimeOther
      : '',
    exposure_frequency: currentCoshhDocument.exposureFrequency,
    exposure_frequency_other:
      (
        currentCoshhDocument.exposureFrequency ===
        comparisonValueForCustomValueUsage
      ) ?
        currentCoshhDocument.exposureFrequencyOther
      : '',
    location: currentCoshhDocument.location,
    level_of_risk: currentCoshhDocument.levelOfRisk,
    additional_information: currentCoshhDocument.additionalInformation,
    assigned_persons_at_risks_attributes: currentCoshhDocument.personsAtRisk,
    persons_at_risk_other:
      (
        currentCoshhDocument.personsAtRisk.find(
          (personAtRisk) =>
            personAtRisk.name === comparisonValueForCustomValueUsage,
        )
      ) ?
        currentCoshhDocument.personsAtRiskOther
      : '',
  };

  const coshhDocumentUpdateParams = () => {
    const updatedPersonsAtRisk = updatedPersonsAtRiskValues(
      initialPersonsAtRisk,
      currentCoshhDocument.personsAtRisk,
    );

    return {
      quantity_on_site: currentCoshhDocument.quantityOnSite,
      quantity_to_be_used: currentCoshhDocument.quantityToBeUsed,
      substance_quantity_per_day: currentCoshhDocument.substanceQuantityPerDay,
      uses: currentCoshhDocument.uses,
      method_of_use_other:
        currentCoshhDocument.uses === comparisonValueForCustomValueUsage ?
          currentCoshhDocument.methodOfUseOther
        : '',
      staff_number: currentCoshhDocument.staffNumber,
      worker_exposure_time: currentCoshhDocument.workerExposureTime,
      worker_exposure_time_other:
        (
          currentCoshhDocument.workerExposureTime ===
          comparisonValueForCustomValueUsage
        ) ?
          currentCoshhDocument.workerExposureTimeOther
        : '',
      exposure_frequency: currentCoshhDocument.exposureFrequency,
      exposure_frequency_other:
        (
          currentCoshhDocument.exposureFrequency ===
          comparisonValueForCustomValueUsage
        ) ?
          currentCoshhDocument.exposureFrequencyOther
        : '',
      location: currentCoshhDocument.location,
      level_of_risk: currentCoshhDocument.levelOfRisk,
      additional_information: currentCoshhDocument.additionalInformation,
      assigned_persons_at_risks_attributes: updatedPersonsAtRisk,
      persons_at_risk_other:
        (
          updatedPersonsAtRisk.find(
            (personAtRisk) =>
              personAtRisk.name === comparisonValueForCustomValueUsage &&
              personAtRisk._destroy === '1',
          )
        ) ?
          ''
        : currentCoshhDocument.personsAtRiskOther,
    };
  };

  const assignCoshhDocumentResponseData = (
    response,
    formattedPersonsAtRisk,
  ) => {
    const { attributes, id } = response;

    return {
      id,
      reference: attributes.reference || '',
      title: attributes.title || '',
      quantityOnSite: attributes.quantityOnSite || '',
      quantityToBeUsed: attributes.quantityToBeUsed || '',
      substanceQuantityPerDay: attributes.substanceQuantityPerDay || '',
      uses: attributes.uses || '',
      methodOfUseOther: attributes.methodOfUseOther || '',
      staffNumber: attributes.staffNumber || '',
      personsAtRisk: formattedPersonsAtRisk,
      personsAtRiskOther: attributes.personsAtRiskOther || '',
      workerExposureTime: attributes.workerExposureTime || '',
      workerExposureTimeOther: attributes.workerExposureTimeOther || '',
      exposureFrequency: attributes.exposureFrequency || '',
      exposureFrequencyOther: attributes.exposureFrequencyOther || '',
      location: attributes.location || '',
      levelOfRisk: attributes.levelOfRisk || '',
      additionalInformation: attributes.additionalInformation || '',
      longTermExposureLimit: attributes.longTermExposureLimit,
      shortTermExposureLimit: attributes.shortTermExposureLimit,
    };
  };

  const handleNewCoshhDocumentSubmit = () => {
    setIsSidePanelSubmitDisabled(true);

    axios
      .post(`/projects/${projectId}/coshh`, {
        master_id: currentCoshhDocument.masterId,
        coshh_document: coshhDocumentCreateParams,
      })
      .then(() => {
        onCoshhDocumentChange();
        resetRequestError();
        refreshResources(pageNumber);
      })
      .catch(handleRequestError);
  };

  const handleAddCoshhOnClick = (masterCoshhDocument) => {
    setSidePanelContext('new');
    setCurrentCoshhDocument(defaultCurrentCoshhDocument(masterCoshhDocument));
    openSidePanel();
  };

  const handleEditCoshhOnClick = (masterCoshhDocument) => {
    const coshhDocument = coshhDocuments.data.find(
      (coshhDoc) =>
        coshhDoc.relationships.masterCoshhDocument.data.id ===
        masterCoshhDocument.id,
    );

    const assignedPersonsAtRisk = coshhDocuments.included.filter(
      (includedResource) =>
        includedResource.relationships.personable.data.id ===
          coshhDocument.id && includedResource.type === 'assignedPersonsAtRisk',
    );

    const formattedPersonsAtRisk = formatPersonsAtRiskValues(
      assignedPersonsAtRisk,
    );

    setInitialPersonsAtRisk(formattedPersonsAtRisk);
    setCurrentCoshhDocument(
      assignCoshhDocumentResponseData(coshhDocument, formattedPersonsAtRisk),
    );
    setSidePanelContext('edit');
    openSidePanel();
  };

  const handleEditCoshhDocumentSubmit = () => {
    setIsSidePanelSubmitDisabled(true);

    axios
      .patch(`/projects/${projectId}/coshh/${currentCoshhDocument.id}`, {
        coshh_document: coshhDocumentUpdateParams(),
        skip_version_generation: true,
      })
      .then(() => {
        onCoshhDocumentChange();
        refreshResources(pageNumber);
        closeSidePanel();
        resetRequestError();
      })
      .catch(handleRequestError);
  };

  const handleCloseProjectCoshhSidePanel = () => {
    resetRequestError();
    closeSidePanel();
  };

  const handleSdsPreviewClick = (masterCoshhDocument) => {
    setPreviewMasterCoshhDocumentId(masterCoshhDocument.id);
    openSdsSidePanel();
  };

  const handleDeleteCoshhOnClick = (masterCoshhDocument) => {
    setCurrentCoshhDocument(defaultCurrentCoshhDocument(masterCoshhDocument));
    setIsProjectCoshhDestroyModalOpen(true);
  };

  const deleteCoshhDocument = () => {
    axios
      .delete(`/projects/${projectId}/coshh/${currentCoshhDocument.id}`, {
        data: { skip_version_generation: true },
      })
      .then(() => {
        onCoshhDocumentChange();
        refreshResources(pageNumber);
      })
      .catch(() => breadBoard.addInedibleToast());
  };

  useEffect(() => {
    if (requestError) {
      setIsSidePanelSubmitDisabled(false);
    }
  }, [requestError]);

  const previewedMasterCoshhDocument = masterCoshhDocuments.data.find(
    (masterCoshhDocument) =>
      masterCoshhDocument.id === previewMasterCoshhDocumentId,
  );

  return (
    masterCoshhDocuments.loaded && (
      <>
        <RowBar
          additionalClasses='tw-mt-7.5'
          content={
            <SearchField
              additionalClasses='search-field__coshh'
              autoFocus={true}
              name='masterCoshhSearch'
              onChange={handleSearchInputChange}
              onReset={handleSearchReset}
              placeholder={i18nT('search.placeholder')}
              value={currentSearch.masterCoshhSearch}
            />
          }
          modifiers={['border-top-curved', 'border-bottom-none']}
        />
        <Table
          blankState={
            currentSearch.masterCoshhSearch && (
              <MasterCoshhSearchBlankNotice
                masterCoshhSearch={currentSearch.masterCoshhSearch}
              />
            )
          }
          headers={
            <tr>
              <th className='tw-w-[5%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900' />
              <th className='tw-w-[40%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('projects.coshh.coshhAssessment')}
              </th>
              <th className='tw-w-[20%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('projects.manufacturer')}
              </th>
              <th className='tw-w-[10%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('shared.reference')}
              </th>
              <th className='tw-w-[15%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('projects.coshh.latestSDSDate')}
              </th>
              <th className='tw-w-[10%] tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900'>
                {i18nTCommon('projects.coshh.previewSDS')}
              </th>
              <th className='tw-w-[10%] tw-text-right tw-text-s tw-font-medium tw-tracking-wide tw-text-grey-900' />
            </tr>
          }
          rows={masterCoshhDocuments.data.map(
            (masterCoshhDocument) =>
              masterCoshhDocument.attributes.published && (
                <MasterCoshhRow
                  isAddedToProject={
                    coshhDocuments.loaded &&
                    Object.prototype.hasOwnProperty.call(
                      coshhDocuments.allMasterCoshhDocumentIds,
                      masterCoshhDocument.id,
                    )
                  }
                  key={masterCoshhDocument.id}
                  masterCoshhDocument={masterCoshhDocument}
                  onAdd={handleAddCoshhOnClick}
                  onDelete={handleDeleteCoshhOnClick}
                  onEdit={handleEditCoshhOnClick}
                  onSdsPreviewClick={handleSdsPreviewClick}
                />
              ),
          )}
        />
        {masterCoshhDocuments.metaData.totalPages > 1 && (
          <div className='tw-my-5 tw-text-center'>
            <Paginator
              currentPage={masterCoshhDocuments.metaData.currentPage}
              onClick={handlePageChange}
              totalPages={masterCoshhDocuments.metaData.totalPages}
            />
          </div>
        )}
        {isProjectCoshhDestroyModalOpen && currentCoshhDocument && (
          <ProjectCoshhDestroyModal
            currentCoshhDocument={currentCoshhDocument}
            onClose={() => setIsProjectCoshhDestroyModalOpen(false)}
            onDelete={() => {
              setIsProjectCoshhDestroyModalOpen(false);
              deleteCoshhDocument();
            }}
          />
        )}
        <ProjectCoshhSidePanel
          coshhDocument={currentCoshhDocument}
          isOpen={currentCoshhDocument.title && isSidePanelOpen}
          isSubmitDisabled={submitDisabled || isSidePanelSubmitDisabled}
          onClose={handleCloseProjectCoshhSidePanel}
          onCoshhInputChange={handleCoshhInputChange}
          onCoshhOptionChange={handleCoshhOptionChange}
          onEditCoshhDocumentSubmit={handleEditCoshhDocumentSubmit}
          onNewCoshhDocumentSubmit={handleNewCoshhDocumentSubmit}
          onOptionSelect={handleOptionSelect}
          removeErrorStyling={removeErrorStyling}
          requestError={requestError}
          setSidePanelContext={setSidePanelContext}
          sidePanelContext={sidePanelContext}
        />
        <MasterCoshhSdsSidePanel
          isOpen={isSdsSidePanelOpen}
          masterCoshhDocument={previewedMasterCoshhDocument}
          onClose={closeSdsSidePanel}
        />
      </>
    )
  );
}

MasterCoshhTable.propTypes = {
  onCoshhDocumentChange: PropTypes.func.isRequired,
};
